import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Calendar, User, DollarSign, CheckCircle, XCircle, Clock } from 'lucide-react';
import { formatNyDateTime } from '../../utils/datetime';

const RentalRequestCard = ({ rental, truck, onApprove, onReject, language = 'pt-BR' }) => {
  const statusColors = {
    pendente: 'bg-yellow-100 border-yellow-300 text-yellow-800',
    aprovado: 'bg-green-100 border-green-300 text-green-800',
    rejeitado: 'bg-red-100 border-red-300 text-red-800',
    ativo: 'bg-blue-100 border-blue-300 text-blue-800',
    finalizado: 'bg-gray-100 border-gray-300 text-gray-800'
  };

  const statusText = {
    pendente: language === 'pt-BR' ? 'Pendente' : 'Pending',
    aprovado: language === 'pt-BR' ? 'Aprovado' : 'Approved',
    rejeitado: language === 'pt-BR' ? 'Rejeitado' : 'Rejected',
    ativo: language === 'pt-BR' ? 'Ativo' : 'Active',
    finalizado: language === 'pt-BR' ? 'Finalizado' : 'Completed'
  };

  const calcularDias = () => {
    if (!rental.data_fim_solicitada) return null;
    const inicio = new Date(rental.data_inicio_solicitada);
    const fim = new Date(rental.data_fim_solicitada);
    return Math.floor((fim - inicio) / (1000 * 60 * 60 * 24)) + 1;
  };

  const dias = calcularDias();

  return (
    <Card className={`border-l-4 ${rental.status === 'pendente' ? 'border-l-yellow-500' : 'border-l-gray-300'}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <Badge className={statusColors[rental.status]}>
              {statusText[rental.status]}
            </Badge>
          </div>
          <p className="text-xs text-gray-500">
            {formatNyDateTime(rental.data_solicitacao)}
          </p>
        </div>

        <h3 className="font-bold text-lg">🚛 {truck?.modelo} ({truck?.placa})</h3>
        
        <div className="mt-3 space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-gray-500" />
            <span><strong>{language === 'pt-BR' ? 'PM:' : 'PM:'}</strong> {rental.pm_name}</span>
          </div>

          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-gray-500" />
            <span><strong>{language === 'pt-BR' ? 'Início:' : 'Start:'}</strong> {new Date(rental.data_inicio_solicitada).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' })}</span>
          </div>

          {rental.data_fim_solicitada ? (
            <>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-gray-500" />
                <span><strong>{language === 'pt-BR' ? 'Fim:' : 'End:'}</strong> {new Date(rental.data_fim_solicitada).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' })}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-gray-500" />
                <span><strong>{language === 'pt-BR' ? 'Período:' : 'Period:'}</strong> {dias} {language === 'pt-BR' ? 'dias' : 'days'}</span>
              </div>
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-gray-500" />
                <span><strong>{language === 'pt-BR' ? 'Estimativa:' : 'Estimate:'}</strong> ${dias * 100}</span>
              </div>
            </>
          ) : (
            <div className="flex items-center gap-2 text-orange-600">
              <Clock className="h-4 w-4" />
              <span className="text-xs italic">{language === 'pt-BR' ? 'Em aberto - definir na devolução' : 'Open - define at return'}</span>
            </div>
          )}

          {rental.proposito && (
            <div className="mt-2 bg-gray-50 p-2 rounded">
              <p className="text-xs text-gray-600">{language === 'pt-BR' ? 'Propósito:' : 'Purpose:'}</p>
              <p className="text-sm">{rental.proposito}</p>
            </div>
          )}
        </div>

        {rental.status === 'pendente' && onApprove && (
          <div className="flex gap-2 mt-4">
            <Button onClick={() => onApprove(rental)} size="sm" className="flex-1 bg-green-600 hover:bg-green-700">
              <CheckCircle className="h-4 w-4 mr-1" />
              {language === 'pt-BR' ? 'Aprovar' : 'Approve'}
            </Button>
            <Button onClick={() => onReject(rental)} size="sm" variant="destructive" className="flex-1">
              <XCircle className="h-4 w-4 mr-1" />
              {language === 'pt-BR' ? 'Rejeitar' : 'Reject'}
            </Button>
          </div>
        )}

        {rental.status === 'rejeitado' && rental.motivo_rejeicao && (
          <div className="mt-3 bg-red-50 p-2 rounded border border-red-200">
            <p className="text-xs text-red-600 font-semibold">{language === 'pt-BR' ? 'Motivo:' : 'Reason:'}</p>
            <p className="text-sm text-red-700">{rental.motivo_rejeicao}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RentalRequestCard;